/** The service popover and what a service does (moved from `quote/quote-editors.tsx` at step 8). */
import { useEffect, useState } from 'react'
import { type QuoteServiceView } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { request } from '../../lib/api.js'
import { Band, Button, Checkbox } from '../../components/primitives.js'
import { MenuSelect } from '../../components/menu-select.js'
import { FieldShell } from '../../components/fields.js'
import { EditorActions, EditorHeader, EditorInput, EditorShell, FooterTotal } from '../editor-shell.js'
import { formatNumber, formatRate } from '../../lib/format.js'
import { OptionsBlock } from '../fields/options-block.js'
import { NotesField } from '../fields/notes-field.js'
import { SERVICE_NOTE_HINTS, SERVICE_TOTAL } from '../copy.js'
import { BIG_BOX, BIG_INPUT } from './big-field.js'

// ---------------------------------------------------------------------------
// §5.4 / §5.5 — the service editor and what a service does
// ---------------------------------------------------------------------------

/**
 * One of the quote's lines as Done would leave it — the server's
 * `planRescale`, never the bare recipe (Aaron, step 21 item 3: the card drew
 * the recipe, so it promised numbers Done did not write).
 */
export interface ServicePlanLineView {
  id: string
  name: string
  unit: string
  optional: boolean
  qtyNow: number
  qty: number
  /** Why it stays put: typed over, or nothing in the recipe names it. */
  kept: 'typed' | 'unmatched' | null
  /** The recipe's rule at this measure, for a line that moves. */
  rule: { baseQty: number; qtyPerUnit: number; bandFrom: number | null } | null
}

export function ServicePopover({
  service, divisions, quoteId, propertyId, quoteReference, canMeasure,
  anchor, onClose, onSave, onResetMeasure, onClearMeasure, onRemove, fromPackage, catalogNotes,
}: {
  service: QuoteServiceView
  /**
   * A package's service — `ServiceRow`'s prop of the same name. It is measured
   * on the package, not here, and it carries the catalog service's own name
   * (a package keeps none of its own), so the measure is absent and the name
   * is read rather than typed. Its notes *are* its own (0127), so the Notes
   * block draws either way.
   */
  fromPackage?: boolean
  /**
   * The catalog service's own three notes, which a package's service follows
   * until it says otherwise (0127) — given, a changed note offers **Use the
   * catalog's words** (step 25).
   */
  catalogNotes?: { customer: string; internal: string; crew: string }
  divisions: { id: string; name: string }[]
  quoteId: string
  /** The job's property, which is where a measure belongs (§4.6). */
  propertyId: string | null
  quoteReference: string
  /** Whether this person may edit the property. The offer is hidden if not. */
  canMeasure: boolean
  anchor: HTMLElement | null
  onClose: () => void
  onSave: (patch: Record<string, unknown>) => Promise<boolean>
  /** Back to the property's measurement, and every recipe line with it. */
  onResetMeasure: () => Promise<boolean>
  /** No measure at all: every line stays, at what it needs at any size. */
  onClearMeasure: () => Promise<boolean>
  /** Take the service off the visit — the line popup's Remove, one level up. */
  onRemove?: () => void
}) {
  const [name, setName] = useState(service.name)
  const [divisionId, setDivisionId] = useState<string | null>(service.divisionId)
  const [measure, setMeasure] = useState(service.measure === null ? '' : String(service.measure))
  const [optional, setOptional] = useState(service.optional)
  const [taxable, setTaxable] = useState(service.taxable)
  /*
   * Pre-filled with what the catalog said when it was added — the quote's own
   * copy since (0125). On a package's service, what the package says, which is
   * the catalog's words until somebody changes them here (0127).
   */
  const [noteCustomer, setNoteCustomer] = useState(service.noteCustomer)
  const [noteInternal, setNoteInternal] = useState(service.noteInternal)
  const [noteCrew, setNoteCrew] = useState(service.noteCrew)
  const [busy, setBusy] = useState(false)
  const [saveToProperty, setSaveToProperty] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  /*
   * Only a service the catalog prices by a measurement has a measure here
   * (Aaron, step 21 item 2). A catalog service on No measure used to get the
   * box anyway, and whatever number sat in it was projected through per-unit
   * rates the catalog page no longer showed.
   */
  const measures = service.serviceId !== null && !fromPackage && service.measurementTypeId !== null

  /*
   * What the measure in the box would produce, fetched as it is typed.
   *
   * The same projection the expansion uses, so the card is a promise rather
   * than an estimate — §5.5's whole point is that somebody can see what one
   * number does before pressing anything.
   */
  const [projection, setProjection] = useState<ServicePlanLineView[] | null>(null)
  useEffect(() => {
    const value = Number(measure)
    if (!measures || !Number.isFinite(value) || value <= 0) { setProjection(null); return }
    let live = true
    const timer = window.setTimeout(() => {
      void request<{ lines: ServicePlanLineView[] }>(
        `/quotes/${quoteId}/services/${service.id}/projection?measure=${value}`)
        .then((body) => { if (live) setProjection(body.lines) })
        .catch(() => { if (live) setProjection(null) })
    }, 250)
    return () => { live = false; window.clearTimeout(timer) }
  }, [measure, measures, service.id, quoteId])

  /*
   * The property disagrees with the box, and something can be done about it.
   *
   * §4.6 makes the property the site of truth, so a measure typed on a quote
   * is a measurement of that site that has not been written down. Offering to
   * write it down is the whole of this: it is a tick nobody has to tick, and
   * the next quote for this property starts from the better number.
   */
  const typed = Number(measure)
  const offerSave = canMeasure
    && propertyId !== null
    && service.measurementTypeId !== null
    && Number.isFinite(typed) && typed > 0
    && typed !== service.propertyMeasure

  /** Reset and Clear: save now, and put the box where the server put it. */
  async function act(run: () => Promise<boolean>, next: number | null) {
    setBusy(true)
    try {
      if (await run()) {
        setMeasure(next === null ? '' : String(next))
        setSaveToProperty(false)
      }
    } finally {
      setBusy(false)
    }
  }

  async function done() {
    setBusy(true)
    try {
      const ok = await onSave({
        name: name.trim() || service.name,
        divisionId: divisionId ?? service.divisionId,
        ...(measures && Number(measure) > 0 && Number(measure) !== service.measure
          ? { measure: Number(measure) }
          : {}),
        optional,
        // Only a change is written: an unchanged box is not a decision to audit.
        ...(typeof service.taxable === 'boolean' && taxable !== service.taxable ? { taxable } : {}),
        // The same rule for the three notes: only the ones that moved.
        ...(noteCustomer !== service.noteCustomer ? { noteCustomer } : {}),
        ...(noteInternal !== service.noteInternal ? { noteInternal } : {}),
        ...(noteCrew !== service.noteCrew ? { noteCrew } : {}),
      })
      if (!ok) return

      /*
       * After the quote, not before: if the quote would not take the number
       * there is no reason to put it on the property. A failure here is worth
       * saying out loud rather than swallowing — the person ticked a box and
       * has a right to know it did not happen — but it does not undo the
       * quote edit, which stands on its own.
       */
      if (offerSave && saveToProperty) {
        try {
          await request(`/measurements/${propertyId}/${service.measurementTypeId}`, {
            method: 'PUT',
            body: {
              value: typed,
              source: 'typed',
              note: `Typed on quote ${quoteReference}.`,
            },
          })
        } catch {
          setSaveToProperty(false)
          setProblem('The quote saved. The property did not take the measurement — '
            + 'it can be set on the property itself.')
          return
        }
      }
      onClose()
    } finally {
      setBusy(false)
    }
  }

  /*
   * "Enhancements & installs · 6 lines · from 640 sq ft" — what this service
   * is, the way the line popup's subtitle says what a line is. The header
   * used to be the catalogue name on its own, which does not distinguish two
   * services of the same name on two visits.
   */
  // "Service · Lawn care · 2 lines" (Board 7).
  const facts = [
    'Service',
    divisions.find((division) => division.id === divisionId)?.name ?? service.divisionName,
    `${service.lines.length} line${service.lines.length === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' · ')

  return (
    <EditorShell
      open
      onClose={onClose}
      anchor={anchor}
      label={service.name}
      header={<EditorHeader kind="service" name={service.name} facts={facts} />}
      footer={(
        <EditorActions
          fact={<FooterTotal label={SERVICE_TOTAL} amount={formatRate(service.total)} />}
          onRemove={onRemove ? () => { onRemove(); onClose() } : undefined}
          onDone={() => void done()}
          busy={busy}
        />
      )}
    >
      <div className="flex flex-col gap-3.5">
        {problem && <Band tone="amber">{problem}</Band>}

        {/*
          * The measure first and big, the way the line popup leads with the
          * quantity: on a service it is the one number that moves every line
          * underneath, and it was the fourth field down in a column of five.
          */}
        {measures && (
          <FieldShell
            label="Measure"
            hint={'Every line below is scaled from this. Lines you have typed over keep their'
              + ' number — Reset puts them back too.'}
          >
            <div className="flex gap-2">
              <span data-editor-input="" data-size="big" className={cn(BIG_BOX, 'min-w-0 flex-1')}>
                <input
                  autoFocus
                  value={measure}
                  inputMode="decimal"
                  aria-label="Measure"
                  onChange={(event) => setMeasure(event.target.value)}
                  className={BIG_INPUT}
                />
                <span className="shrink-0 text-[12px] font-medium text-text-faint">
                  {service.measureUnit ?? ''}
                </span>
              </span>
              {/*
                * Two ways back, beside the box they act on (Aaron, 11 Oct).
                * **Reset** is the property's own figure and every recipe line
                * redone from it, typed-over ones included; **Clear** is no
                * measure at all, every line kept at what it needs at any size.
                * Each saves at once — they are actions, not a draft for Done —
                * and "From map" is gone until measuring from a quote is a
                * feature rather than a link to the property.
                */}
              <Button
                size="toolbar"
                className="h-11 shrink-0"
                disabled={busy || service.propertyMeasure === null}
                title={service.propertyMeasure === null
                  ? `The property has no ${
                    (service.measurementName ?? 'measurement').toLowerCase()} yet`
                  : `Back to the property's ${formatNumber(service.propertyMeasure)} ${
                    service.measureUnit ?? ''}, and every line back to the recipe`}
                onClick={() => void act(onResetMeasure, service.propertyMeasure)}
              >
                Reset
              </Button>
              <Button
                size="toolbar"
                className="h-11 shrink-0"
                disabled={busy || (service.measure === null && measure.trim() === '')}
                title="No measure: every line stays, at what it needs at any size"
                onClick={() => void act(onClearMeasure, null)}
              >
                Clear
              </Button>
            </div>
          </FieldShell>
        )}

        <div className="grid grid-cols-2 gap-3">
          <FieldShell label="Name">
            <EditorInput
              autoFocus={!service.serviceId}
              value={name}
              readOnly={fromPackage}
              aria-label="Service name"
              onChange={(event) => setName(event.target.value)}
            />
          </FieldShell>
          <FieldShell label="Division" hint="Every number this service makes lands here.">
            <MenuSelect
              size="field"
              label="Division"
              value={divisionId}
              onChange={setDivisionId}
              options={divisions.map((division) => ({
                value: division.id, label: division.name,
              }))}
            />
          </FieldShell>
        </div>

        {offerSave && (
          <Checkbox
            label={service.propertyMeasure === null
              ? `Save it to the property as its ${
                (service.measurementName ?? 'measurement').toLowerCase()}`
              : `Update the property's ${
                (service.measurementName ?? 'measurement').toLowerCase()} — it says ${
                formatNumber(service.propertyMeasure)} ${service.measureUnit ?? ''}`}
            checked={saveToProperty}
            onChange={(event) => setSaveToProperty(event.target.checked)}
          />
        )}

        {projection && (
          <ServiceScalingCard
            lines={projection}
            measure={Number(measure)}
            unit={service.measureUnit ?? ''}
          />
        )}

        {/*
          * **Notes** (Aaron's review item 3): the visit's Customer · Internal ·
          * Crew block, pre-filled from the catalog service and the quote's
          * own from then on. Customer is what the proposal prints under this
          * service. A package's service wears it too (0127) — it was hidden
          * there, which left a package no way to say anything about a service
          * but what the catalog said — and Done hands the moved notes to the
          * package builder, which keeps them as the package's own words.
          */}
        <NotesField
          quote={noteCustomer}
          internal={noteInternal}
          crew={noteCrew}
          onQuote={setNoteCustomer}
          onInternal={setNoteInternal}
          onCrew={setNoteCrew}
          hints={SERVICE_NOTE_HINTS}
          follows={catalogNotes && {
            quote: catalogNotes.customer, internal: catalogNotes.internal, crew: catalogNotes.crew,
          }}
        />

        {/*
          * Always last (§4.3). cb-20 cell 2: Optional and Taxable — a taxable
          * service taxes every line it adds. Board 7's Show on proposal has
          * no column on a quote service yet (step-20 DEVIATION REQUEST).
          */}
        <OptionsBlock
          level="service"
          optional={{ checked: optional, onChange: setOptional }}
          taxable={typeof service.taxable === 'boolean'
            ? { checked: taxable, onChange: setTaxable }
            : undefined}
        />
      </div>
    </EditorShell>
  )
}

/**
 * How a line's quantity is arrived at, in one phrase.
 *
 *   base only   → "once"            (a mobilization, a dump run)
 *   rate only   → "0.0222 / sq ft"  (the ordinary case)
 *   both        → "4 + 0.0027 / sq ft"
 */
function formula(rule: NonNullable<ServicePlanLineView['rule']>, lineUnit: string, unit: string): string {
  const rate = `${rule.qtyPerUnit} ${lineUnit} / ${unit}`
  if (rule.qtyPerUnit === 0) return rule.baseQty === 1 ? 'once' : `${rule.baseQty} ${lineUnit}`
  if (rule.baseQty === 0) return rate
  return `${rule.baseQty} + ${rate}`
}

/** Why a line keeps its number, in the words the grid's own hint uses. */
const KEPT_WORDS = {
  typed: 'typed over · keeps',
  unmatched: 'not in the recipe · keeps',
} as const

/**
 * §5.5 — "0.0222 ton / sq ft → 14.2 ton", once per line on the quote.
 *
 * The quote's lines and not the recipe's: a line somebody typed over, and a
 * line the recipe no longer names, are drawn with the number they will
 * *keep*, so the card is a promise about what Done writes rather than a
 * picture of what the catalog would do on a fresh quote.
 */
export function ServiceScalingCard({
  lines, measure, unit,
}: {
  lines: ServicePlanLineView[]
  measure: number
  unit: string
}) {
  const kept = lines.filter((line) => line.kept !== null).length
  return (
    <section data-scaling-card="" className="rounded-field border border-line bg-surface-alt px-3 py-2.5">
      <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-text-faint">
        What {formatNumber(measure)} {unit} does
      </p>
      <dl className="m-0 flex flex-col gap-1">
        {lines.map((line) => (
          <div key={line.id} data-kept={line.kept ?? undefined} className="flex items-baseline gap-2 text-[12px]">
            <span className="min-w-0 flex-1 truncate text-text">{line.name}</span>
            <span className={cn('shrink-0', line.kept ? 'text-warn' : 'text-text-faint')}>
              {line.kept
                ? KEPT_WORDS[line.kept]
                : line.rule && formula(line.rule, line.unit, unit)}
              {line.rule?.bandFrom != null && (
                <span className="ml-1 text-accent">
                  · {formatNumber(line.rule.bandFrom)}+
                </span>
              )}
            </span>
            <span className="tnum w-[120px] shrink-0 text-right font-semibold text-text">
              {line.kept || line.qty === line.qtyNow
                ? `${formatNumber(line.qty)} ${line.unit}`
                : `${formatNumber(line.qtyNow)} → ${formatNumber(line.qty)} ${line.unit}`}
            </span>
          </div>
        ))}
      </dl>
      <p className="m-0 mt-2 border-t border-line-soft pt-2 text-[11px] leading-[1.45] text-text-faint">
        {kept === 0
          ? 'Done rescales every line above. Each one stays editable on the quote.'
          : `Done rescales every line but ${kept === 1 ? 'the one' : `the ${kept}`} marked keeps.`
            + ' Reset puts typed-over lines back to the recipe too.'}
      </p>
    </section>
  )
}
